import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { fromEvent, interval, merge, Observable, Subscription, timer, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { DialogService } from '../../components/dialog/dialog.service';
import { UserService } from './user.service';
import { UserAuth } from './user.auth';
import { ApiService } from '../api/api.service';

@Injectable({
  providedIn: 'root'
})
export class UserIdleService {
  readonly unsubscribe: Subject<void> = new Subject();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _inactivityTimerEvent: Array<any>[] = [
    [document, 'click'],
    [document, 'wheel'],
    [document, 'scroll'],
    [document, 'mousemove'],
    [document, 'keyup'],
    [window, 'resize'],
    [window, 'scroll'],
    [window, 'mousemove']
  ];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _events$: Observable<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _observableFilter$: Observable<any>;
  private _observableFilterSubscription: Subscription;
  private _intervalSubscription: Subscription;
  private _promptTimeoutSubscription: Subscription;
  private _inactivityTime = 0;
  private _timeLapsedSinceInactivity = 0;
  private _promptTimeout = 10;
  private _fusionAuthConfigURL = '/fusionui/authconfig';
  private _refreshTokenURL = '/ims/v2/users/me';
  private _refreshToken = false;
  private _timeStart: any;

  constructor(
    public _ngZone: NgZone,
    private readonly router: Router,
    private dialogService: DialogService,
    private readonly userAuth: UserAuth,
    private readonly userService: UserService,
    private readonly apiService: ApiService
  ) {
    if (this.userService.profileEvents) {
      this.userService.profileEvents.subscribe(event => {
        this.handleProfileEvent(event);
      });
    }
  }

  public setUserIdleTime(refreshToken: boolean = false): void {
    this._refreshToken = refreshToken;
    const idleTimes = this.userAuth.getUserIdleTimes();
    if (idleTimes.idle !== 0) {
      // if monitor user (0), then set no idle timer
      this.apiService
        //eslint-disable-next-line
        .get$<any>(this._fusionAuthConfigURL, {})
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          response => {
            if (response && response.userIdleTimer) {
              idleTimes.idle = response.userIdleTimer * 60 - idleTimes.timeout;
            }
            this.checkUserIdle(idleTimes);
          },
          //eslint-disable-next-line
          error => {
            this.checkUserIdle(idleTimes);
          },
          () => {}
        );
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  public checkUserIdle(userIdleTimes: any): void {
    this._inactivityTime = userIdleTimes.idle;
    this._promptTimeout = userIdleTimes.timeout;

    if (this._inactivityTime !== 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const event$: Observable<any>[] = [];
      this._inactivityTimerEvent.forEach(x => {
        event$.push(fromEvent(x[0], x[1]));
      });
      this._events$ = merge(...event$);
      this._createObservable();
    }
  }

  public onExit(): void {
    if (!this._intervalSubscription?.closed) {
      this._intervalSubscription?.unsubscribe();
    }
    if (!this._intervalSubscription?.closed) {
      this._promptTimeoutSubscription?.unsubscribe();
    }

    if (!this._observableFilterSubscription?.closed) {
      this._observableFilterSubscription?.unsubscribe();
    }

    this._clearAllEvents();
  }

  private _createObservable(): void {
    this._ngZone.runOutsideAngular(() => {
      this._timeLapsedSinceInactivity = this._inactivityTime;
      this._timeStart = Date.now();

      this._intervalSubscription = interval(1000).subscribe(() => {
        const elapsed = (Date.now() - this._timeStart) / 1000;
        if (elapsed >= this._timeLapsedSinceInactivity) {
          this._intervalSubscription.unsubscribe();
          this._promptUserToKeepAlive();
        }
      });

      this._observableFilter$ = this._events$.pipe(
        map(() => {
          this._timeLapsedSinceInactivity = this._inactivityTime;
          this._timeStart = Date.now();
        })
      );

      this._observableFilterSubscription = this._observableFilter$.subscribe();
    });
  }

  private _promptUserToKeepAlive(): void {
    const params = {
      id: 'user.idle.timer',
      callBackService: this,
      callBackFnParams: {},
      callBackFuntion: function (service) {
        service._resetTimer();
      }
    };

    this._ngZone.run(() => {
      this.dialogService.showDialog('confirm', 'body', params);
    });

    this._promptTimeoutSubscription = timer(this._promptTimeout * 1000).subscribe(() => {
      this._promptTimeoutSubscription.unsubscribe();
      this._clearAllEvents();

      this._ngZone.run(() => {
        console.log('user inactive--logging out');
        this.dialogService.closeAll();
        this.userService.signout();
      });
    });
  }

  private _resetTimer(): void {
    this._promptTimeoutSubscription.unsubscribe();
    this._createObservable();

    if (this._refreshToken) {
      this.apiService
        //eslint-disable-next-line
        .get$<any>(this._refreshTokenURL, {})
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          //eslint-disable-next-line
          response => {},
          //eslint-disable-next-line
          error => {},
          () => {}
        );
    }
  }

  private _clearAllEvents(): void {
    this._events$ = null;
  }

  private handleProfileEvent(event: any): void {
    //eslint-disable-line
    if (event?.event === 'logout') {
      this.router.navigate(['logout']);
    }
  }
}
